import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { UserListApiModel } from '@app/api/api-models/users/UserListApiModel';
import { BulkProfileUpdateApiModel } from '@app/api/api-models/users/BulkProfileUpdateApiModel';
import { UserSignedUpApiModel } from '@app/api/api-models/users/UserSignedUpModel';
import { BulkRestoreUserResultApiModel } from '@app/api/api-models/users/BulkRestoreUserResultApiModel';
import { BulkProgramRoleUpdateApiModel } from '@app/api/api-models/users/BulkProgramRoleUpdateApiModel';
import { BulkProfileUpdateResultApiModel } from '@app/api/api-models/users/BulkProfileUpdateResultApiModel';
import { UploadFileService } from '@app/shared/services/upload-file.service';
import { ImportResultApiModel } from '@app/api/api-models/ImportResultApiModel';
import { HttpClientService } from '@app/api/api-services/http-client.service';

@Injectable()
export class UserManagementService {
  public apiUrl = 'users';

  constructor(private http: HttpClientService, private readonly uploadFileService: UploadFileService) {}

  public getList(): Observable<UserListApiModel[]> {
    return this.http.get<UserListApiModel[]>(`${this.apiUrl}/get-list`);
  }

  public shiftBulkUpdate(model: BulkProfileUpdateApiModel): Observable<UserListApiModel[]> {
    return this.http.post<UserListApiModel[]>(`${this.apiUrl}/shift-bulk-update`, model);
  }

  public locationBulkUpdate(model: BulkProfileUpdateApiModel): Observable<UserListApiModel[]> {
    return this.http.post<UserListApiModel[]>(`${this.apiUrl}/location-bulk-update`, model);
  }

  public groupBulkUpdate(model: BulkProfileUpdateApiModel): Observable<UserListApiModel[]> {
    return this.http.post<UserListApiModel[]>(`${this.apiUrl}/group-bulk-update`, model);
  }

  public programRoleBulkUpdate(model: BulkProgramRoleUpdateApiModel): Observable<BulkProfileUpdateResultApiModel> {
    return this.http.post<BulkProfileUpdateResultApiModel>(`${this.apiUrl}/program-role-bulk-update`, model);
  }

  public bulkDelete(userIds: string[]): Observable<BulkProfileUpdateResultApiModel> {
    return this.http.post<BulkProfileUpdateResultApiModel>(`${this.apiUrl}/bulk-delete`, userIds);
  }

  public bulkRestore(userIds: string[]): Observable<BulkRestoreUserResultApiModel> {
    return this.http.post<BulkRestoreUserResultApiModel>(`${this.apiUrl}/bulk-restore`, userIds);
  }

  public getImportTemplateExample(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/get-import-template-example`, { responseType: 'blob' });
  }

  public importXlsx(file: File): Observable<ImportResultApiModel> {
    const payload = new FormData();
    this.uploadFileService.append(payload, file);

    return this.http.post(`${this.apiUrl}/import-xlsx`, payload, { responseType: 'blob', observe: 'response' }).pipe(
      map((data: any) => {
        const model = new ImportResultApiModel();
        model.Blob = data.body;

        if (data.headers.has('Import-Results')) {
          const parts = data.headers.get('Import-Results').split(';');
          model.FailedCount = parseInt(parts[0] || '0', 10);
          model.ImportedCount = parseInt(parts[1] || '0', 10);
          model.ShiftsAdded = parseInt(parts[2] || '0', 10);
          model.LocationsAdded = parseInt(parts[3] || '0', 10);
          model.ShiftsFailed = parseInt(parts[4] || '0', 10);
          model.LocationsFailed = parseInt(parts[5] || '0', 10);
        }

        return model;
      })
    );
  }

  public getSignUpDate(): Observable<UserSignedUpApiModel> {
    return this.http.get(`${this.apiUrl}/get-created-at`);
  }
}
